// addEditDeviceUtils.js
import dayjs from "dayjs";

/**
 * Initial form state for Add/Edit Device
 */
export const getInitialFormData = () => ({
  type: {},
  name: "",
  deviceMake: "",
  cameraType: "",
  streamUrl: "",
  hlsUrl: "",
  status: 0,
  serialNumber: "",
  dateInstalled: null,
  macAddress: "",
  ipAddress: "",
  topicName: "",
  triggers: [0],
  cssXCoordinate: 0,
  cssYCoordinate: 0,
  niagaraDeviceName: "",
});

/**
 * IP validation helpers
 */
const ipRegex = {
  v4: /^(\d{1,3}\.){3}\d{1,3}$/,
  v6: /^([\da-f]{1,4}:){7}[\da-f]{1,4}$/i,
};

export const isValidIpAddress = (ipAddress = "") => {
  if (!ipAddress) return false;
  return ipRegex.v4.test(ipAddress) || ipRegex.v6.test(ipAddress);
};

/**
 * MAC validation
 */
export const isValidMacAddress = (macAddress = "") =>
  /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/.test(macAddress);

/**
 * Main validation for Add/Edit Device form.
 * Returns { isValid, errors }
 */
export const validateDeviceForm = ({
  formData,
  actionButton,
  existingDeviceNames = [],
}) => {
  let isValid = true;
  const errors = {};

  if (!formData.type?.id) {
    errors.type = "Please enter a device type";
    isValid = false;
  }

  const requiredFields = [
    "name",
    "deviceMake",
    "serialNumber",
    "dateInstalled",
    "macAddress",
    "ipAddress",
  ];

  if (actionButton !== "add") requiredFields.push("topicName");

  // if (
  //   Object.keys(formData?.type || {}).length > 0 &&
  //   formData?.type?.name?.toLowerCase().includes("camera")
  // ) {
  //   requiredFields.push("hlsUrl");
  // }

  requiredFields.forEach((field) => {
    if (!formData[field]) {
      errors[field] = `Please enter ${field
        .replace(/([A-Z])/g, " $1")
        .toLowerCase()}`;
      isValid = false;
    }
  });

  if (
    actionButton !== "edit" &&
    formData.name &&
    existingDeviceNames.includes(formData.name)
  ) {
    errors.name = "Device name must be unique. This name is already taken.";
    isValid = false;
  }

  if (!formData.dateInstalled || !formData.dateInstalled?.isValid?.()) {
    errors.dateInstalled = "Please enter a valid installation date";
    isValid = false;
  }

  if (formData.type?.name === "Camera") {
    if (!formData.cameraType) {
      errors.cameraType = "Please enter a Camera type";
      isValid = false;
    }

    if (!/^(https?|rtsp):\/\/.+/.test(formData.streamUrl || "")) {
      errors.streamUrl = "Please enter a valid stream URL";
      isValid = false;
    }
  }

  if (!isValidIpAddress(formData.ipAddress)) {
    errors.ipAddress = "Please enter a valid IPv4 or IPv6 address";
    isValid = false;
  }

  if (!isValidMacAddress(formData.macAddress)) {
    errors.macAddress = "Please enter a valid MAC address";
    isValid = false;
  }

  if (actionButton === "add") {
    delete errors.topicName;
  }

  return { isValid, errors };
};

/**
 * Parse triggeredBy from editData to an array of numbers
 */
export const parseTriggeredByToArray = (triggeredBy) => {
  let triggersArray = [0];

  if (triggeredBy === undefined || triggeredBy === null) {
    return triggersArray;
  }

  if (Array.isArray(triggeredBy)) {
    return triggeredBy.length ? triggeredBy : [0];
  }

  if (typeof triggeredBy === "string") {
    const trimmed = triggeredBy.trim();
    if (!trimmed || trimmed === "0") return [0];

    const parsed = trimmed
      .split(",")
      .map((item) => parseInt(item.trim(), 10))
      .filter((num) => !Number.isNaN(num));

    return parsed.length ? parsed : [0];
  }

  if (typeof triggeredBy === "number") {
    return [triggeredBy];
  }

  return triggersArray;
};

/**
 * Build initial formData from editData for Edit Device flow
 */
export const getEditFormDataFromEditData = (editData) => {
  const dateInstalledValue = editData.dateInstalled
    ? dayjs(editData.dateInstalled, "YYYY-MM-DD")
    : null;

  const triggersArray = parseTriggeredByToArray(editData.triggeredBy);

  return {
    name: editData.name,
    type: editData.type || {},
    deviceMake: editData.deviceMake,
    status: editData.status,
    serialNumber: editData.serialNumber,
    dateInstalled: dateInstalledValue,
    macAddress: editData.macAddress,
    hlsUrl: editData.hlsStreamUrl,
    ipAddress: editData.ipAddress,
    topicName: editData.topicName,
    cameraType: editData.cameraType,
    streamUrl: editData.streamUrl,
    cssXCoordinate: editData.cssXCoordinate,
    cssYCoordinate: editData.cssYCoordinate,
    niagaraDeviceName: editData.niagaraDeviceName,
    triggers: triggersArray,
  };
};

/**
 * Payload builder for Add Device API
 */
export const buildAddDevicePayload = (formData, userGuid) => ({
  name: formData.name,
  cssXCoordinate: 0,
  cssYCoordinate: 0,
  type: formData.type,
  cameraType: formData.cameraType,
  streamUrl: formData.streamUrl,
  hlsStreamUrl: formData.hlsUrl,
  deviceLocationName: "",
  deviceLocationId: null,
  triggeredByDeviceId: [0],
  deleted: false,
  createdAt: new Date().toISOString(),
  modifiedAt: new Date().toISOString(),
  createdBy: userGuid,
  modifiedBy: userGuid,
  triggeredBy: "0",
  deviceMake: formData.deviceMake,
  status: formData.status,
  serialNumber: formData.serialNumber,
  installed: formData.dateInstalled,
  macAddress: formData.macAddress,
  ipAddress: formData.ipAddress,
  topicName: "homeassistant_niagara/niagara/topic",
  floorId: null,
});

/**
 * Payload builder for Update Device API
 */
export const buildUpdateDevicePayload = ({
  formData,
  editData,
  userGuid,
  checked,
}) => {
  const triggeredByValue =
    Array.isArray(formData.triggers) && formData.triggers.length > 0
      ? formData.triggers.join(",")
      : "0";

  return {
    id: editData.id,
    isNiagaraMapped: checked,
    niagaraDeviceName: formData.niagaraDeviceName,
    name: formData.name,
    type: formData.type,
    cssXCoordinate: editData.cssXCoordinate,
    cssYCoordinate: editData.cssYCoordinate,
    cameraType: formData.cameraType,
    streamUrl: formData.streamUrl,
    hlsStreamUrl: formData.hlsUrl,
    deviceLocationName: "string",
    deviceLocationId: editData.deviceLocationId,
    triggeredByDeviceId: [0],
    deleted: false,
    createdAt: new Date().toISOString(),
    modifiedAt: new Date().toISOString(),
    createdBy: editData.createdBy,
    modifiedBy: userGuid,
    triggeredBy: triggeredByValue,
    deviceMake: formData.deviceMake,
    status: formData.status,
    serialNumber: formData.serialNumber,
    installed: formData.dateInstalled,
    macAddress: formData.macAddress,
    ipAddress: formData.ipAddress,
    topicName: formData.topicName,
  };
};

/**
 * Download bulk upload error CSV (HTML table as .xls)
 */
export const downloadErrorCSV = (errorFile) => {
  if (!errorFile || errorFile.length === 0) {
    console.warn("No errors to download");
    return;
  }

  let table = `
    <table border="1">
      <tr>
        <th>Row Number</th>
        <th>Messages</th>
      </tr>
  `;

  errorFile.forEach((err) => {
    table += `
      <tr>
        <td>${err.rowNumber}</td>
        <td>${err.messages.join("; ")}</td>
      </tr>
    `;
  });

  table += `</table>`;

  const blob = new Blob([table], {
    type: "application/vnd.ms-excel",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "bulk-upload-errors.xls";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Connection class for Test Connection button
 */
export const getConnectionClass = (testConnectionStatus) => {
  if (testConnectionStatus === "success") return "scs";
  if (testConnectionStatus === "error") return "err";
  return "";
};

/**
 * Topic field display helper (current behaviour = just return value)
 */
export const getFormattedValue = (formData, name) => {
  const value = formData[name] || "";
  return value;
};
